import React, { useMemo, useRef, useState } from "react";
import api from "./api/client";
import AccessManager from "./AccessManager";

/** ===== Helpers for local date ===== */
function pad2(n: number) { return n.toString().padStart(2, "0"); }
function formatLocalDate(d: Date) {
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  return `${y}-${m}-${day}`; // YYYY-MM-DD in local time
}
function startOfDayLocal(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}
function addDaysLocal(d: Date, delta: number) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta, 0, 0, 0, 0);
}
function startOfWeekLocal(d: Date) {
  const day = d.getDay(); // 0=Sun,..6=Sat
  const diff = (day === 0 ? -6 : 1 - day); // make Monday start
  return startOfDayLocal(addDaysLocal(d, diff));
}

/** ===== Types ===== */
type Granularity = 60 | 15;
type Motor = { id: string; name: string };

type EventKind = {
  id: number;        // numeric ID
  label: string;     // display name
  bg: string;        // CSS fill color
  border: string;    // CSS border color
};

type MotorEvent = {
  id: string;
  motorId: string;
  start: string; // ISO
  end: string;   // ISO
  typeId: number;
  note?: string;
  label?: string; // (UI nepoužívá)
  createdById?: string;
};

type StoreShape = {
  motors: Motor[];
  byDate: Record<string, MotorEvent[]>;
  kinds: EventKind[];
};

// anti-double-alert helper (0.8 s okno)
let __lastWarnAt = 0;
function alertOnce(msg: string) {
  const now = Date.now();
  if (now - __lastWarnAt < 800) return;
  __lastWarnAt = now;
  window.alert(msg);
}

/** ===== Utils ===== */
function toISO(date: Date) { const d = new Date(date); d.setSeconds(0, 0); return d.toISOString(); }
function withTime(baseISODateLocal: string, hours: number, minutes: number) {
  const [datePart] = baseISODateLocal.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const dt = new Date((y as number), (m as number) - 1, (d as number), 0, 0, 0, 0);
  dt.setHours(hours, minutes, 0, 0);
  return dt;
}
function clamp(min: number, v: number, max: number) { return Math.max(min, Math.min(v, max)); }
function roundToGranularity(mins: number, g: Granularity) {
  const r = Math.round(mins / g) * g;
  return clamp(0, r, 1440);
}

/** minutes from midnight of event start (handles DST/24:00) */
function startMin(ev: MotorEvent) {
  const s = new Date(ev.start);
  const dayStart = startOfDayLocal(s);
  return clamp(0, Math.round((s.getTime() - dayStart.getTime()) / 60000), 1440);
}
function endMin(ev: MotorEvent) {
  const s = new Date(ev.start), e = new Date(ev.end);
  const dayStart = startOfDayLocal(s);
  return clamp(0, Math.round((e.getTime() - dayStart.getTime()) / 60000), 1440);
}

/** ===== Defaults (seed pro typy) ===== */
const defaultKinds: EventKind[] = [
  { id: 1, label: "Must Work",         bg: "#bae6fd", border: "#38bdf8" },
  { id: 2, label: "Must be Idle",      bg: "#e5e7eb", border: "#9ca3af" },
  { id: 3, label: "Ancillary Service", bg: "#99f6e4", border: "#14b8a6" },
  { id: 4, label: "Flexibility",       bg: "#e9d5ff", border: "#a855f7" },
  { id: 5, label: "Failure",           bg: "#fecdd3", border: "#fb7185" },
  { id: 6, label: "Planned Outage",    bg: "#fde68a", border: "#f59e0b" },
];

/** Admin-only helper (Rezervace SVR) */
function isReservationKindLabel(label: string) {
  return /rezervace\s*svr/i.test(label) || /reservation/i.test(label);
}

/** ===== Planner lock D+2 ve 20:00 (lokální čas) ===== */
function isPlannerLockedForDay(dayKey: string, now: Date = new Date()) {
  // dayKey = "YYYY-MM-DD" (lokální)
  const [y, m, d] = dayKey.split("-").map(Number);
  const eventDay = new Date((y as number), (m as number) - 1, (d as number), 0, 0, 0, 0);
  // Lock time = (eventDay - 2 dny) v 20:00
  const lockBase = addDaysLocal(eventDay, -2);
  const lockAt = withTime(`${formatLocalDate(lockBase)}T00:00:00`, 20, 0);
  return now >= lockAt;
}
function isPlannerLockedForISO(iso: string) {
  return isPlannerLockedForDay(formatLocalDate(new Date(iso)));
}

/** ===== Edit dialog for event ===== */
function EditDialog({
  open, onClose, event, onSave, kinds, role,
}: {
  open: boolean;
  event: MotorEvent | null;
  kinds: EventKind[];
  role: 'ADMIN'|'PLANNER'|'VIEWER'|null;
  onClose: () => void;
  onSave: (e: MotorEvent) => void;
}) {
  const [local, setLocal] = useState({
    typeId: kinds[0]?.id ?? 1,
    start: "00:00",
    end: "01:00",
    note: "",
  });

  const isAdmin = role === 'ADMIN';
  const reservationIds = useMemo(
    () => kinds.filter(k => isReservationKindLabel(k.label)).map(k => k.id),
    [kinds]
  );
  const wasReservation = !!(event && reservationIds.includes(event.typeId));
  const isSelectingReservation = reservationIds.includes(local.typeId);

  React.useEffect(() => {
    if (!event) return;
    const sM = startMin(event);
    const eM = endMin(event);
    setLocal({
      typeId: event.typeId,
      start: `${pad2(Math.floor(sM / 60))}:${pad2(sM % 60)}`,
      end: eM === 1440 ? "23:59" : `${pad2(Math.floor(eM / 60))}:${pad2(eM % 60)}`,
      note: event.note ?? "",
    });
  }, [event]);

  if (!open || !event) return null;
  const dateStr = formatLocalDate(new Date(event.start));

  function parseHM(s: string) {
    const [h, m] = s.split(":").map(Number);
    return { h: clamp(0, h || 0, 23), m: clamp(0, m || 0, 59) };
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-[560px] rounded-2xl bg-white p-5 shadow-xl" lang="cs">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Upravit událost</h2>
          <button className="rounded-xl px-3 py-1 text-sm hover:bg-gray-100" onClick={onClose}>Zavřít</button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-sm text-gray-600">Datum</label>
            <input className="w-full rounded-xl border p-2" type="date" value={dateStr} disabled />
          </div>

          {/* Typ – pro ne-adminy zamezíme výběru Rezervace SVR.
              Pokud už událost je Rezervace SVR a user není admin, typ zamkneme read-only. */}
          <div>
            <label className="mb-1 block text-sm text-gray-600">Typ</label>

            {!isAdmin && wasReservation ? (
              <div className="w-full rounded-xl border p-2 bg-gray-50 text-gray-700">
                {kinds.find(k => k.id === local.typeId)?.label ?? local.typeId}
                <div className="mt-1 text-xs text-gray-500">(Typ nelze měnit – pouze administrátor)</div>
              </div>
            ) : (
              <select
                className="w-full rounded-xl border p-2"
                value={local.typeId}
                onChange={(e) => setLocal({ ...local, typeId: Number(e.target.value) })}
              >
                {kinds.map((k) => {
                  const isReservation = isReservationKindLabel(k.label);
                  const disabled = !isAdmin && isReservation; // blokuj Rezervaci SVR pro ne-adminy
                  return (
                    <option key={k.id} value={k.id} disabled={disabled}>
                      {k.label}{disabled ? " (jen pro admina)" : ""}
                    </option>
                  );
                })}
              </select>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-600">Začátek</label>
            <input
              className="w-full rounded-xl border p-2"
              type="time"
              lang="cs"
              step={60}
              max="23:59"
              value={local.start}
              onChange={(e) => setLocal({ ...local, start: e.target.value })}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600">Konec</label>
            <input
              className="w-full rounded-xl border p-2"
              type="time"
              lang="cs"
              step={60}
              max="23:59"
              value={local.end}
              onChange={(e) => setLocal({ ...local, end: e.target.value })}
            />
          </div>

          <div className="col-span-2">
            <label className="mb-1 block text-sm text-gray-600">Poznámka</label>
            <textarea
              className="w-full rounded-xl border p-2"
              rows={3}
              value={local.note}
              onChange={(e) => setLocal({ ...local, note: e.target.value })}
            />
          </div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button className="rounded-xl border px-3 py-2 text-sm hover:bg-gray-50" onClick={onClose}>Zrušit</button>
          <button
            className="rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
            onClick={() => {
              // 🔒 VIEWER je read-only
              if (role === 'VIEWER') { alertOnce("Nemáš oprávnění upravovat."); return; }
              // Gate: planner lock na den události
              if (role === 'PLANNER' && isPlannerLockedForISO(event.start)) {
                alertOnce("Úpravy jsou pro tento den uzamčené (D+2 ve 20:00).");
                return;
              }
              // Gate: ne-admin nesmí nastavovat Rezervace SVR
              if (!isAdmin) {
                if (!wasReservation && isSelectingReservation) {
                  alert('Typ „Rezervace SVR“ může nastavovat pouze administrátor.');
                  return;
                }
                if (wasReservation && local.typeId !== event.typeId) {
                  alert('Změnu typu u „Rezervace SVR“ může provádět pouze administrátor.');
                  return;
                }
              }

              const parseHM = (s: string) => {
                const [h, m] = s.split(":").map(Number);
                return { h: clamp(0, h || 0, 23), m: clamp(0, m || 0, 59) };
              };

              const s = parseHM(local.start);
              const e = parseHM(local.end);
              const base = `${formatLocalDate(new Date(event.start))}T00:00:00`;

              const startDate = withTime(base, s.h, s.m);
              let endDate =
                (local.end === "23:59" || (e.h === 23 && e.m === 59))
                  ? withTime(base, 24, 0)
                  : withTime(base, e.h, e.m);

              if (endDate <= startDate) {
                window.alert("Konec musí být po začátku");
                return;
              }

              onSave({
                ...event!,
                start: toISO(startDate),
                end: toISO(endDate),
                typeId: local.typeId,
                note: local.note,
                label: undefined,
              });
              onClose();
            }}
          >
            Uložit
          </button>
        </div>
      </div>
    </div>
  );
}

/** ===== Type Manager (číselník) ===== */
function TypeManager({
  open, kinds, onClose, onSave, onDeleteKind,
}: {
  open: boolean;
  kinds: EventKind[];
  onClose: () => void;
  onSave: (next: EventKind[]) => void;
  onDeleteKind: (kindId: number, removeEvents: boolean) => void;
}) {
  const [local, setLocal] = useState<EventKind[]>(kinds);
  React.useEffect(() => setLocal(kinds), [kinds]);
  if (!open) return null;

  function update(i: number, patch: Partial<EventKind>) {
    setLocal((arr) => arr.map((k, idx) => (idx === i ? { ...k, ...patch } : k)));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-[720px] max-w-[96vw] rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Typy událostí</h2>
          <button className="rounded-xl px-3 py-1 text-sm hover:bg-gray-100" onClick={onClose}>Zavřít</button>
        </div>

        <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
          {local.map((k, i) => (
            <div key={k.id} className="flex items-center gap-2 rounded-xl border p-3">
              <div className="h-6 w-6 rounded-full border" style={{ backgroundColor: k.bg, borderColor: k.border }} />
              <span className="rounded-md bg-gray-100 px-2 py-1 text-[11px] text-gray-600">id: {k.id}</span>
              <input
                className="w-48 rounded-lg border px-2 py-1"
                value={k.label}
                onChange={(e) => update(i, { label: e.target.value })}
              />
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600">Výplň</label>
                <input type="color" value={k.bg} onChange={(e) => update(i, { bg: e.target.value })} />
                <label className="ml-3 text-xs text-gray-600">Okraj</label>
                <input type="color" value={k.border} onChange={(e) => update(i, { border: e.target.value })} />
              </div>
              <div className="ml-auto">
                <button
                  className="rounded-lg border px-2 py-1 text-sm hover:bg-red-50 hover:text-red-700"
                  onClick={() => {
                    const removeEvents = window.confirm("Smazat tento typ a VŠECHNY jeho události (ve všech dnech)?\nZvol 'Storno' pro remap na jiný typ.");
                    onDeleteKind(k.id, removeEvents);
                  }}
                >
                  Smazat typ
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <button
            className="rounded-lg border px-3 py-2 text-sm hover:bg-gray-50"
            onClick={() => {
              const nextId = (local.length ? Math.max(...local.map(k => k.id)) : 0) + 1;
              setLocal((arr) => [...arr, { id: nextId, label: `Typ ${nextId}`, bg: "#e5e7eb", border: "#9ca3af" }]);
            }}
          >
            + Přidat typ (auto ID)
          </button>
          <div className="flex items-center gap-2">
            <button className="rounded-lg border px-3 py-2 text-sm hover:bg-gray-50" onClick={onClose}>Zrušit</button>
            <button
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
              onClick={() => { onSave(local.map(k => ({ ...k, label: k.label.trim() || String(k.id) }))); onClose(); }}
            >
              Uložit číselník
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** ===== Main component ===== */
export default function MotorScheduler() {
  // view mode: day/week
  const [view, setView] = useState<'day'|'week'>('day');

  // always start from local "today"
  const [date, setDate] = useState(() => startOfDayLocal(new Date()));
  const dateKey = useMemo(() => formatLocalDate(date), [date]);
  const baseDateLocalISO = `${dateKey}T00:00:00`;

  // DENNÍ pohled má 1h granularitu
  const granularity: Granularity = 60;

  // Store
  const [motors, setMotors] = useState<Motor[]>([]);
  const [kinds, setKinds] = useState<EventKind[]>([]);
  const [byDate, setByDate] = useState<Record<string, MotorEvent[]>>({});
  const [events, setEvents] = useState<MotorEvent[]>([]);
  const [editing, setEditing] = useState<MotorEvent | null>(null);
  const [typesOpen, setTypesOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [role, setRole] = useState<'ADMIN'|'PLANNER'|'VIEWER'|null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  // Výběr typu pro vytváření
  const [selectedKindId, setSelectedKindId] = useState<number | null>(null);
  React.useEffect(() => {
    if (selectedKindId == null && kinds.length) setSelectedKindId(kinds[0].id);
  }, [kinds, selectedKindId]);

  // --- chipy pro předvolbu typu (používá selectedKindId) ---
  const KindChips: React.FC = () => (
    <div className="flex flex-wrap items-center gap-2">
      {kinds.map((k) => {
        const active = selectedKindId === k.id;
        const adminOnly = isReservationKindLabel(k.label);
        const disabled = adminOnly && role !== 'ADMIN';
        return (
          <button
            key={k.id}
            onClick={() => { if (!disabled) setSelectedKindId(k.id); }}
            className={`flex items-center gap-2 rounded-2xl border px-3 py-1 text-sm ${
              active ? "ring-2 ring-gray-900 border-gray-900 shadow-sm" : "hover:bg-gray-50"
            } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            title={disabled ? "Tento typ může vytvářet jen administrátor." : "Vyber typ pro vytváření"}
          >
            <span
              className="inline-block h-3 w-3 rounded-full border"
              style={{ backgroundColor: k.bg, borderColor: k.border }}
            />
            {k.label}
          </button>
        );
      })}
    </div>
  );

  const slots = useMemo(() => (24 * 60) / granularity, [granularity]);

  async function loadDay(key: string, kindsRef: EventKind[]) {
    const jres = await api.get('/jobs', { params: { date: key } });
    const jobs = Array.isArray(jres.data) ? jres.data : [];
    const eventsFromApi: MotorEvent[] = jobs.map((j: any) => {
      const k = kindsRef.find((kk: any) => String(kk.bg || '').toLowerCase() === String(j.color || '').toLowerCase());
      const typeId = k ? k.id : kindsRef[0]?.id;
      return ({
        id: j.id,
        motorId: j.motorId,
        start: j.start,
        end: j.end,
        typeId,
        label: j.title,
        note: j.note ?? undefined,
        createdById: j.createdById ?? undefined,
      });
    });
    return eventsFromApi;
  }

  // vytvoření eventu pro konkrétní den (např. z týdenního kliknutí)
  async function addEventOnDay(motorId: string, dayKey: string, typeId?: number) {
    // 🔒 VIEWER je read-only
    if (role === 'VIEWER') { alertOnce("Nemáš oprávnění vytvářet bloky."); return; }
    // Planner lock
    if (role === 'PLANNER' && isPlannerLockedForDay(dayKey)) {
      alertOnce("Vytváření je pro tento den uzamčeno (D+2 ve 20:00).");
      return;
    }
    const k = kinds.find(x => x.id === (typeId ?? selectedKindId ?? kinds[0]?.id));
    if (!k) return;
    if (isReservationKindLabel(k.label) && role !== 'ADMIN') {
      alert('Typ „Rezervace SVR“ může vytvářet pouze administrátor.');
      return;
    }
    const baseISO = `${dayKey}T00:00:00`;
    const start = toISO(withTime(baseISO, 0, 0));
    const end = toISO(withTime(baseISO, 24, 0));
    const color = k.bg;
    const label = k.label || "Úkol";
    try {
      const res = await api.post('/jobs', { motorId, title: label, start, end, color });
      const ev: MotorEvent = { id: res.data.id, motorId, start, end, typeId: k.id, createdById: userId || undefined };
      setByDate(prev => {
        const next = { ...prev };
        const arr = (next[dayKey] ?? []).slice();
        arr.push(ev);
        next[dayKey] = arr;
        return next;
      });
      if (dayKey === dateKey) setEvents(prev => [...prev, ev]);
    } catch (e) {
      console.error("Failed to create full-day job", e);
      alert("Nepodařilo se vytvořit celodenní úkol.");
    }
  }

  // LOAD from API
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const mres = await api.get('/motors');
        if (!alive) return;
        setMotors(mres.data || []);

        let kinds0 = defaultKinds;
        try {
          const kres = await api.get('/kinds');
          const arr = Array.isArray(kres.data) ? kres.data : [];
          kinds0 = arr.length ? arr : defaultKinds;
          if (!arr.length) { try { await api.put('/kinds', kinds0); } catch {} }
        } catch (e) { console.error('GET /kinds failed, using defaults', e); }
        setKinds(kinds0);
        try {
          const me = await api.get('/me');
          setRole(me.data?.membership?.role ?? null);
          setUserId(me.data?.user?.id ?? me.data?.id ?? null);
        } catch {}
        if (view === 'day') {
          const dayEv = await loadDay(dateKey, kinds0);
          if (!alive) return;
          setByDate({ [dateKey]: dayEv });
          setEvents(dayEv);
        } else {
          const monday = startOfWeekLocal(date);
          const keys: string[] = Array.from({ length: 7 }, (_, i) => formatLocalDate(addDaysLocal(monday, i)));
          const arrs = await Promise.all(keys.map(k => loadDay(k, kinds0)));
          if (!alive) return;
          const map: Record<string, MotorEvent[]> = {};
          keys.forEach((k, i) => (map[k] = arrs[i]));
          setByDate(map);
          setEvents(map[dateKey] ?? []);
        }
      } catch (e) {
        console.error("API init failed", e);
      } finally {
        setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [dateKey, view]);

  React.useEffect(() => {
    if (loaded) setEvents(byDate[dateKey] ?? []);
  }, [dateKey, byDate, loaded]);

  function commitEvents(updater: (prev: MotorEvent[]) => MotorEvent[]) {
    setEvents((prev) => {
      const resolved = updater(prev);
      setByDate((map) => ({ ...map, [dateKey]: resolved }));
      return resolved;
    });
  }

  function dayKeyFromISO(iso: string) {
    return formatLocalDate(new Date(iso));
  }

  function upsertEventEverywhere(ev: MotorEvent) {
    setByDate((map) => {
      const next: Record<string, MotorEvent[]> = {};
      for (const k of Object.keys(map)) next[k] = (map[k] ?? []).filter(x => x.id !== ev.id);
      const dk = dayKeyFromISO(ev.start);
      next[dk] = [...(next[dk] ?? []), ev].sort((a,b) => +new Date(a.start) - +new Date(b.start));
      return next;
    });

    const currentDayKey = dateKey;
    const evDayKey = dayKeyFromISO(ev.start);

    if (currentDayKey === evDayKey) {
      setEvents((prev) => prev.filter(x => x.id !== ev.id).concat(ev)
        .sort((a,b) => +new Date(a.start) - +new Date(b.start)));
    } else {
      setEvents((prev) => prev.filter(x => x.id !== ev.id));
    }
  }

// smaže událost všude
function removeEventEverywhere(id: string) {
  setByDate((map) => {
    const next: Record<string, MotorEvent[]> = {};
    for (const k of Object.keys(map)) next[k] = (map[k] ?? []).filter(e => e.id !== id);
    return next;
  });
  setEvents((prev) => prev.filter(e => e.id !== id));
}

  function addEvent(motorId: string, startMinV: number, endMinV: number, typeId?: number) {
    // 🔒 VIEWER je read-only
    if (role === 'VIEWER') { alertOnce("Nemáš oprávnění vytvářet bloky."); return; }
    // Planner lock na aktuální den
    if (role === 'PLANNER' && isPlannerLockedForDay(dateKey)) {
      alertOnce("Vytváření je pro tento den uzamčeno (D+2 ve 20:00).");
      return;
    }

    if (!typeId) typeId = selectedKindId ?? kinds[0]?.id;
    if (!typeId) return;

    if (startMinV === endMinV) { startMinV = 0; endMinV = 1440; }

    const startDate = withTime(baseDateLocalISO, Math.floor(startMinV / 60), startMinV % 60);
    const endDate   = withTime(baseDateLocalISO, Math.floor(endMinV   / 60), endMinV   % 60);
    const start = toISO(startDate);
    const end   = toISO(endDate);

    const k = kinds.find((x) => x.id === typeId);
    if (!k) return;
    if (isReservationKindLabel(k.label) && role !== 'ADMIN') {
      alert('Typ „Rezervace SVR“ může vytvářet pouze administrátor.');
      return;
    }
    const color = k.bg;
    const label = k.label || "Úkol";

    (async () => {
      try {
        const res = await api.post('/jobs', { motorId, title: label, start, end, color });
        const ev: MotorEvent = { id: res.data.id, motorId, start, end, typeId: typeId!, createdById: userId || undefined };
        commitEvents((prev) => [...prev, ev]);
      } catch (e) {
        console.error("Failed to create job", e);
        alert("Nepodařilo se vytvořit úkol.");
      }
    })();
  }

  function updateEventTime(id: string, startMinV: number, endMinV: number) {
    // 🔒 VIEWER je read-only
    if (role === 'VIEWER') { alertOnce("Nemáš oprávnění upravovat."); return; }
    // Planner lock na aktuální den
    if (role === 'PLANNER' && isPlannerLockedForDay(dateKey)) {
      alertOnce("Úpravy jsou pro tento den uzamčené (D+2 ve 20:00).");
      return;
    }

    startMinV = clamp(0, startMinV, 1440);
    endMinV = clamp(0, endMinV, 1440);
    const startISO = toISO(withTime(baseDateLocalISO, Math.floor(startMinV / 60), startMinV % 60));
    const endISO   = toISO(withTime(baseDateLocalISO, Math.floor(endMinV   / 60), endMinV   % 60));

    const prevOne = (byDate[dateKey] ?? []).concat(events).find(e => e.id === id);
    const merged: MotorEvent = { ...(prevOne as MotorEvent), start: startISO, end: endISO };

    upsertEventEverywhere(merged);

    (async () => {
      try { await api.patch(`/jobs/${id}`, { start: startISO, end: endISO }); }
      catch (e) { console.error("Failed to update job time", e); }
    })();
  }

  function upsertEvent(ev: MotorEvent) {
    upsertEventEverywhere(ev);
    const k = kinds.find((x) => x.id === ev.typeId);
    const payload: any = { start: ev.start, end: ev.end, color: k?.bg, note: ev.note };
    (async () => {
      try {
        await api.patch(`/jobs/${ev.id}`, payload);
      } catch (e) {
        console.error("Failed to update job", e);
      }
    })();
  }

  function deleteEvent(id: string) {
    // 🔒 VIEWER je read-only
    if (role === 'VIEWER') { alertOnce("Nemáš oprávnění mazat."); return; }

    // Najdi event kvůli dni (pro lock kontrolu)
    const ev =
      (byDate[dateKey] ?? []).concat(events).find(e => e.id === id) ||
      Object.values(byDate).flat().find(e => e.id === id);

    if (role === 'PLANNER' && ev && isPlannerLockedForISO(ev.start)) {
      alertOnce("Mazání je pro tento den uzamčeno (D+2 ve 20:00).");
      return;
    }

    removeEventEverywhere(id);
    (async () => {
      try {
        await api.delete(`/jobs/${id}`);
      } catch (err: any) {
        console.error("Failed to delete job", err);
        if (err?.response?.status === 403) {
          alert("Nemáš právo smazat tento blok (můžeš jen vlastní).");
        } else {
          alert("Nepodařilo se smazat úkol.");
        }
        try {
          const days = Object.keys(byDate);
          const arrs = await Promise.all(days.map(k => loadDay(k, kinds)));
          const map: Record<string, MotorEvent[]> = {};
          days.forEach((k, i) => (map[k] = arrs[i]));
          setByDate(map);
          setEvents(map[dateKey] ?? []);
        } catch {}
      }
    })();
  }

  // motors
  async function addMotor() {
    if (role !== 'ADMIN') return;
    const name = window.prompt("Název motoru:");
    if (!name) return;
    try {
      const res = await api.post('/motors', { name: name.trim() });
      setMotors((ms) => [...ms, res.data]);
    } catch (e) {
      console.error("Failed to create motor", e);
      alert("Nepodařilo se vytvořit motor.");
    }
  }

  async function deleteMotor(id: string) {
    if (role !== 'ADMIN') return;
    if (!window.confirm("Smazat motor včetně jeho událostí?")) return;
    try {
      await api.delete(`/motors/${id}`);
      setMotors((ms) => ms.filter((m) => m.id !== id));
      setByDate((map) => {
        const copy: Record<string, MotorEvent[]> = {};
        for (const k of Object.keys(map)) copy[k] = (map[k] ?? []).filter((e) => e.motorId !== id);
        return copy;
      });
      setEvents((prev) => prev.filter((e) => e.motorId !== id));
    } catch (e) {
      console.error("Failed to delete motor", e);
      alert("Nepodařilo se smazat motor.");
    }
  }

  // kinds
  async function saveKinds(next: EventKind[]) {
    if (role !== 'ADMIN') return;
    const normalized = next.map(k => ({ ...k, label: k.label.trim() || String(k.id) }));
    const prev = kinds;
    setKinds(normalized);
    try {
      await api.put('/kinds', normalized);
    } catch (e) {
      console.error('Failed to save kinds', e);
      setKinds(prev);
      alert('Nepodařilo se uložit typy událostí na server.');
    }
  }

  async function handleDeleteKind(kindId: number, removeEvents: boolean) {
    if (role !== 'ADMIN') return;
    const prevKinds = kinds;
    const others = prevKinds.filter(k => k.id !== kindId);
    const fallback = others.length ? Math.min(...others.map(k => k.id)) : undefined;

    if (removeEvents || fallback === undefined) {
      setByDate((map) => {
        const copy: Record<string, MotorEvent[]> = {};
        for (const k of Object.keys(map)) copy[k] = (map[k] ?? []).filter((e) => e.typeId !== kindId);
        return copy;
      });
      setEvents((prevE) => prevE.filter((e) => e.typeId !== kindId));
    } else {
      setByDate((map) => {
        const copy: Record<string, MotorEvent[]> = {};
        for (const k of Object.keys(map)) {
          copy[k] = (map[k] ?? []).map((e) => e.typeId === kindId ? { ...e, typeId: fallback } : e);
        }
        return copy;
      });
      setEvents((prevE) => prevE.map((e) => e.typeId === kindId ? { ...e, typeId: fallback } : e));
    }

    setKinds(others);
    try {
      const normalized = others.map(k => ({ ...k, label: k.label.trim() || String(k.id) }));
      await api.put('/kinds', normalized);
    } catch (e) {
      console.error('Failed to save kinds after delete', e);
      setKinds(prevKinds);
      alert('Nepodařilo se uložit změny typů událostí na server. Změna bude vrácena.');
      return;
    }

    const affectedNow = events.filter(e => e.typeId === kindId);
    if (affectedNow.length) {
      if (removeEvents || fallback === undefined) {
        await Promise.allSettled(affectedNow.map(e => api.delete(`/jobs/${e.id}`)));
      } else {
        const fb = others.find(k => k.id === fallback);
        const newColor = fb?.bg;
        await Promise.allSettled(affectedNow.map(e => api.patch(`/jobs/${e.id}`, { color: newColor })));
      }
    }
  }

  /** ===== UI ===== */
  const monday = startOfWeekLocal(date);
  const weekKeys: string[] = Array.from({ length: 7 }, (_, i) => formatLocalDate(addDaysLocal(monday, i)));
  const weekLabels = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];
  const todayKey = formatLocalDate(startOfDayLocal(new Date()));

  return (
    <div className="mx-auto max-w-[1400px] p-4">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            className="rounded-2xl border px-3 py-2 hover:bg-gray-50"
            onClick={() => setDate((d) => view === 'day' ? addDaysLocal(d, -1) : addDaysLocal(d, -7))}
          >
            ◀︎ {view === 'day' ? 'Den' : 'Týden'}
          </button>

          {view === 'day' ? (
            <input
              type="date"
              className="rounded-2xl border px-3 py-2"
              value={dateKey}
              onChange={(e) => {
                const v = e.target.value; // YYYY-MM-DD
                const [y, m, da] = v.split("-").map(Number);
                setDate(new Date((y as number), (m as number) - 1, (da as number), 0, 0, 0, 0));
              }}
            />
          ) : (
            <div className="flex items-center gap-2 rounded-2xl border px-3 py-2">
              <span className="text-sm">{formatLocalDate(monday)} – {formatLocalDate(addDaysLocal(monday, 6))}</span>
            </div>
          )}

          <button
            className="rounded-2xl border px-3 py-2 hover:bg-gray-50"
            onClick={() => setDate((d) => view === 'day' ? addDaysLocal(d, 1) : addDaysLocal(d, 7))}
          >
            {view === 'day' ? 'Den' : 'Týden'} ▶︎
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-2xl border p-1">
            <button
              className={`rounded-2xl px-3 py-1 text-sm ${view === 'day' ? "bg-gray-900 text-white" : "hover:bg-gray-100"}`}
              onClick={() => setView('day')}
            >
              Denní
            </button>
            <button
              className={`rounded-2xl px-3 py-1 text-sm ${view === 'week' ? "bg-gray-900 text-white" : "hover:bg-gray-100"}`}
              onClick={() => setView('week')}
            >
              Týdenní
            </button>
          </div>

          {role === 'ADMIN' && (
            <>
              <button className="rounded-2xl border px-3 py-2 hover:bg-gray-50" onClick={addMotor}>+ Motor</button>
              <button className="rounded-2xl border px-3 py-2 hover:bg-gray-50" onClick={() => setTypesOpen(true)}>Typy událostí</button>
              <button className="rounded-2xl border px-3 py-2 hover:bg-gray-50" onClick={() => setAccessOpen(true)}>Přístupy</button>
            </>
          )}
        </div>
      </div>

      {/* Sticky hlavička pro DEN: časová lišta + chipy */}
      {view === 'day' && (
        <div className="sticky top-0 z-30 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/70">
          <div className="p-2">
            <div className="ml-40 grid" style={{ gridTemplateColumns: `repeat(${slots}, minmax(0, 1fr))` }}>
              {Array.from({ length: slots }, (_, i) => {
                const mins = (i * granularity) % 1440;
                const hr = Math.floor(mins / 60);
                const show = mins % 60 === 0;
                return (
                  <div key={i} className="relative h-8">
                    <div className="absolute inset-y-0 right-0 w-px bg-gray-200" />
                    {show && (
                      <div
                        className="absolute -translate-x-1/2 text-xs text-gray-600"
                        style={{ left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}
                      >
                        {pad2(hr)}:00
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-2">
              <KindChips />
            </div>
          </div>
        </div>
      )}

      {/* Pro TÝDEN chipy nesticky pod hlavičkou */}
      {view === 'week' && (
        <div className="mb-3">
          <KindChips />
        </div>
      )}

      {/* Views */}
      {view === 'day' ? (
        <div className="space-y-3">
          {motors.map((m) => {
            const lockedForPlanner = role === 'PLANNER' && isPlannerLockedForDay(dateKey);
            return (
              <MotorRow
                key={m.id}
                motor={m}
                kinds={kinds}
                events={events.filter((e) => e.motorId === m.id)}
                granularity={granularity}
                role={role}
                userId={userId}
                isLockedForPlanner={lockedForPlanner}
                onCreate={(s, e) => addEvent(m.id, s, e)}
                onEdit={(ev) => setEditing(ev)}
                onDragUpdate={(id, s, e) => updateEventTime(id, s, e)}
                onResizeUpdate={(id, s, e) => updateEventTime(id, s, e)}
                onDelete={(id) => deleteEvent(id)}
                onDeleteMotor={role === 'ADMIN' ? deleteMotor : undefined}
              />
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border bg-white">
          {/* Week header */}
          <div className="flex border-b">
            <div className="w-40 shrink-0 p-3 font-medium">Motor</div>
            <div className="flex grow min-w-0 overflow-x-hidden">
              {weekKeys.map((k, i) => (
                <div
                  key={k}
                  className={`flex-1 border-l p-3 ${k === todayKey ? 'bg-amber-50' : ''}`}
                >
                  <div className="text-sm font-medium">{weekLabels[i]} {k}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Week rows per motor */}
          {motors.map((m) => (
            <div key={m.id} className="flex items-stretch border-b">
              <div className="flex w-40 shrink-0 items-start gap-2 border-r p-3">
                <div className="grow">
                  <div className="font-medium">{m.name}</div>
                  <div className="text-xs text-gray-500">klik na “+ celod.”</div>
                </div>
                {role === 'ADMIN' && (
                  <button
                    className="h-7 w-7 rounded-lg border text-gray-600 hover:bg-gray-50"
                    title="Smazat motor"
                    onClick={() => deleteMotor(m.id)}
                  >
                    ×
                  </button>
                )}
              </div>

              {weekKeys.map((k) => {
                const dayEvents = (byDate[k] ?? []).filter(e => e.motorId === m.id);
                const locked = role === 'PLANNER' && isPlannerLockedForDay(k);
                const canPlannerEditDay = role === 'PLANNER' && !locked;
                const readOnlyDay = role === 'VIEWER' || (role === 'PLANNER' && locked);
                return (
                  <div
                    key={k}
                    className={`group relative flex-1 border-r p-2 min-h-[96px] overflow-hidden ${k === todayKey ? 'bg-amber-50/60' : ''}`}
                  >
                    {/* Přidání celodenního */}
                    <button
                      className="absolute right-1 top-1 rounded-md border bg-white/80 px-2 py-0.5 text-[11px] opacity-0 shadow transition hover:bg-white group-hover:opacity-100"
                      title="Přidat celodenní blok"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (readOnlyDay) {
                          if (role === 'VIEWER') alertOnce("Nemáš oprávnění vytvářet bloky.");
                          else alertOnce("Vytváření je pro tento den uzamčeno (D+2 ve 20:00).");
                          return;
                        }
                        addEventOnDay(m.id, k);
                      }}
                    >
                      + celod.
                    </button>

                    {/* Obsah dne */}
                    {dayEvents.length === 0 ? (
                      <div className="mt-5 text-[12px] text-gray-400">—</div>
                    ) : (
                      <div className="mt-5 flex max-h-[140px] flex-col gap-1 overflow-y-auto overflow-x-hidden">
                        {dayEvents.map(ev => {
                          const kind = kinds.find(x => x.id === ev.typeId);
                          const canDeleteWeek =
                            role === 'ADMIN' ||
                            (role === 'PLANNER' && !locked && !!userId && ev.createdById === userId);
                          const sM = startMin(ev), eM = endMin(ev);
                          const endText = eM === 1440
                            ? "24:00"
                            : `${pad2(Math.floor(eM / 60))}:${pad2(eM % 60)}`;
                          const timeStr = `${pad2(Math.floor(sM / 60))}:${pad2(sM % 60)}–${endText}`;
                          return (
                            <div
                              key={ev.id}
                              data-wk-evt="1"
                              className="flex items-center gap-2 truncate rounded-md border px-2 py-1 text-xs leading-tight"
                              style={{ backgroundColor: kind?.bg || "#f3f4f6", borderColor: kind?.border || "#9ca3af" }}
                              title={`${kind?.label ?? ev.typeId}\n${timeStr}${ev.note ? `\n${ev.note}` : ""}`}
                            >
                              <div className="flex-1 min-w-0 truncate font-semibold">
                                {kind?.label || "Událost"}
                              </div>
                              <div className="ml-2 shrink-0 text-[11px] opacity-80">{timeStr}</div>
                              <button
                                className="shrink-0 rounded border bg-white/70 px-1 py-0.5 hover:bg-white"
                                onClick={(ev2) => {
                                  ev2.stopPropagation();
                                  if (readOnlyDay) {
                                    if (role === 'VIEWER') alertOnce("Nemáš oprávnění upravovat.");
                                    else alertOnce("Úpravy tohoto dne jsou po 20:00 D-2 uzamčené pro plánovače.");
                                    return;
                                  }
                                  setEditing(ev);
                                }}
                                title="Upravit"
                                disabled={readOnlyDay}
                              >
                                ✎
                              </button>

                              {canDeleteWeek && (
                                <button
                                  className="shrink-0 rounded border bg-white/70 px-1 py-0.5 hover:bg-white"
                                  onClick={(ev2) => {
                                    ev2.stopPropagation();
                                    if (!canDeleteWeek) return;
                                    if (window.confirm("Smazat blok?")) deleteEvent(ev.id);
                                  }}
                                  title="Smazat"
                                >
                                  🗑
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <EditDialog open={!!editing} event={editing} kinds={kinds} role={role} onClose={() => setEditing(null)} onSave={(ev) => upsertEvent(ev)} />
      <TypeManager open={typesOpen} kinds={kinds} onClose={() => setTypesOpen(false)} onSave={saveKinds} onDeleteKind={handleDeleteKind} />
      <AccessManager open={accessOpen} onClose={() => setAccessOpen(false)} motors={motors} />
    </div>
  );
}

/** ===== Layout lanes ===== */
type Positioned = { ev: MotorEvent; lane: number };
function layoutLanes(evts: MotorEvent[]): { lanes: Positioned[]; laneCount: number } {
  const sorted = [...evts].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  const laneEnds: number[] = [];
  const result: Positioned[] = [];
  for (const ev of sorted) {
    const s = startMin(ev), e = endMin(ev);
    let placed = false;
    for (let i = 0; i < laneEnds.length; i++) {
      if (laneEnds[i] <= s) { result.push({ ev, lane: i }); laneEnds[i] = e; placed = true; break; }
    }
    if (!placed) { result.push({ ev, lane: laneEnds.length }); laneEnds.push(e); }
  }
  return { lanes: result, laneCount: laneEnds.length || 1 };
}

/** ===== Row with create / drag / resize (locked lane while dragging) ===== */
function MotorRow({
  motor,
  kinds,
  events,
  granularity,
  role,
  userId,
  isLockedForPlanner,
  onCreate,
  onEdit,
  onDragUpdate,
  onResizeUpdate,
  onDelete,
  onDeleteMotor,
}: {
  motor: Motor;
  kinds: EventKind[];
  events: MotorEvent[];
  granularity: Granularity;
  role: 'ADMIN'|'PLANNER'|'VIEWER'|null;
  userId: string | null;
  isLockedForPlanner?: boolean;
  onCreate: (startMin: number, endMin: number) => void;
  onEdit: (event: MotorEvent) => void;
  onDragUpdate: (id: string, startMin: number, endMin: number) => void;
  onResizeUpdate: (id: string, startMin: number, endMin: number) => void;
  onDelete: (id: string) => void;
  onDeleteMotor?: (id: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canPlannerEdit = role === 'PLANNER' && !(isLockedForPlanner ?? false);
  const canEditNow = role === 'ADMIN' || canPlannerEdit;  // VIEWER => false

  function denyMessage() {
    if (role === 'VIEWER') { alertOnce("Nemáš oprávnění provádět změny."); }
    else { alertOnce("Úpravy jsou pro tento den uzamčené (D+2 ve 20:00)."); }
  }

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const dragLaneRef = useRef<number>(0);

  const baseEvents = draggingId ? events.filter((e) => e.id !== draggingId) : events;
  const { lanes: lanesBase, laneCount: laneCountBase } = useMemo(() => layoutLanes(baseEvents), [baseEvents]);

  const effectiveLaneCount = Math.max(laneCountBase, draggingId ? dragLaneRef.current + 1 : laneCountBase);

  const slotCount = (24 * 60) / granularity;
  const laneHeight = 96;
  const laneGap = 8;
  const rowInnerHeight = effectiveLaneCount * laneHeight + Math.max(0, effectiveLaneCount - 1) * laneGap;

  const [select, setSelect] = useState<null | { startMin: number; endMin: number }>(null);
  const creatingRef = useRef(false);
  const createFiredRef = useRef(false);
  const [resize, setResize] = useState<null | { id: string; edge: "left" | "right"; startAtDown: number; endAtDown: number }>(null);

  function clientXToMinute(x: number) {
    const el = containerRef.current; if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const ratio = clamp(0, (x - rect.left) / rect.width, 1);
    return ratio === 1 ? 1440 : ratio * 1440;
  }
  function pxToMinuteSnapped(x: number) { return roundToGranularity(clientXToMinute(x), granularity); }

  function handleBgPointerDown(e: React.PointerEvent) {
    if (e.button !== 0) return;
    if (!canEditNow) { denyMessage(); return; }
    const container = e.currentTarget as Element;
    const rawTarget = e.target as HTMLElement;
    if (rawTarget.closest('[data-evt="1"]')) return;

    container.setPointerCapture?.(e.pointerId);

    const startMinV = pxToMinuteSnapped(e.clientX);
    setSelect({ startMin: startMinV, endMin: startMinV });
    creatingRef.current = true;
    createFiredRef.current = false;

    let raf = 0; let finished = false;
    const schedule = (x: number) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setSelect((s) => (s ? { ...s, endMin: pxToMinuteSnapped(x) } : s));
      });
    };

    const move = (ev: PointerEvent) => schedule(ev.clientX);
    const finish = () => {
      if (finished) return; finished = true;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      container?.removeEventListener?.("lostpointercapture", finish);
      setSelect((s) => {
        creatingRef.current = false;
        if (!s || createFiredRef.current) return null;
        let a = Math.min(s.startMin, s.endMin);
        let b = Math.max(s.startMin, s.endMin);
        if (a === b) { a = 0; b = 1440; }
        if (b - a >= granularity) { createFiredRef.current = true; onCreate(a, b); }
        return null;
      });
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish, { once: true });
    window.addEventListener("pointercancel", finish, { once: true });
    container.addEventListener?.("lostpointercapture", finish, { once: true });
  }

  function startDragEvent(e: React.PointerEvent, ev: MotorEvent) {
    // DŮLEŽITÉ: nejdřív zastavit bublání a default
    e.stopPropagation();
    e.preventDefault?.();
    if (e.button !== 0) return;

    // Lock/oprávnění kontrola až po stopPropagation
    if (!canEditNow) { denyMessage(); return; }

    const t = e.target as HTMLElement;
    if (t.closest('[data-ctrl="1"]')) return;
    if (creatingRef.current) return;

    const target = e.currentTarget as Element;
    target.setPointerCapture?.(e.pointerId);

    const currentLane = (() => {
      const full = layoutLanes(events).lanes;
      const found = full.find((p) => p.ev.id === ev.id);
      return found ? found.lane : 0;
    })();
    dragLaneRef.current = currentLane;
    setDraggingId(ev.id);

    const s0 = startMin(ev), e0 = endMin(ev);
    const pointerDownMin = clientXToMinute(e.clientX);
    const duration = e0 - s0;

    let raf = 0;
    const schedule = (x: number) => {
      if (raf) return; raf = requestAnimationFrame(() => {
        raf = 0;
        const pointerNow = clientXToMinute(x);
        const unroundedStart = s0 + (pointerNow - pointerDownMin);
        let newStart = roundToGranularity(unroundedStart, granularity);
        newStart = clamp(0, newStart, 1440 - duration);
        const newEnd = newStart + duration;
        onDragUpdate(ev.id, newStart, newEnd);
      });
    };

    const move = (ev2: PointerEvent) => schedule(ev2.clientX);
    const finish = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      target?.removeEventListener?.("lostpointercapture", finish);
      setDraggingId(null);
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish, { once: true });
    window.addEventListener("pointercancel", finish, { once: true });
    target.addEventListener?.("lostpointercapture", finish, { once: true });
  }

  function startResize(e: React.PointerEvent, ev: MotorEvent, edge: "left" | "right") {
    // DŮLEŽITÉ: nejdřív zastavit bublání a default
    e.stopPropagation();
    e.preventDefault?.();
    if (e.button !== 0) return;

    // Lock/oprávnění kontrola až po stopPropagation
    if (!canEditNow) { denyMessage(); return; }

    const target = e.currentTarget as Element;
    target.setPointerCapture?.(e.pointerId);

    const currentLane = (() => {
      const full = layoutLanes(events).lanes;
      const found = full.find((p) => p.ev.id === ev.id);
      return found ? found.lane : 0;
    })();
    dragLaneRef.current = currentLane;
    setDraggingId(ev.id);

    const s0 = startMin(ev), e0 = endMin(ev);
    setResize({ id: ev.id, edge, startAtDown: s0, endAtDown: e0 });

    let raf = 0;
    const schedule = (x: number) => {
      if (raf) return; raf = requestAnimationFrame(() => {
        raf = 0;
        setResize((r) => {
          if (!r) return null;
          const snap = pxToMinuteSnapped(x);
          let newStart = r.startAtDown;
          let newEnd = r.endAtDown;
          if (r.edge === "left") newStart = clamp(0, snap, newEnd - granularity);
          else newEnd = clamp(newStart + granularity, snap, 1440);
          onResizeUpdate(r.id, newStart, newEnd);
          return { ...r };
        });
      });
    };

    const move = (ev2: PointerEvent) => schedule(ev2.clientX);
    const finish = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      target?.removeEventListener?.("lostpointercapture", finish);
      setResize(null);
      setDraggingId(null);
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish, { once: true });
    window.addEventListener("pointercancel", finish, { once: true });
    target.addEventListener?.("lostpointercapture", finish, { once: true });
  }

  function fmt(ev: MotorEvent) {
    const s = startMin(ev), e = endMin(ev);
    const endText = e === 1440 ? "24:00" : `${pad2(Math.floor(e / 60))}:${pad2(e % 60)}`;
    return `${pad2(Math.floor(s / 60))}:${pad2(s % 60)}–${endText}`;
  }

  function widthForRange(sMin: number, eMin: number) {
    if (sMin === 0 && eMin === 1440) return "100%";
    return `${((eMin - sMin) / 1440) * 100}%`;
  }

  function renderBox(ev: MotorEvent, lane: number, extra?: { z?: number; ghost?: boolean }) {
    const s = startMin(ev);
    const e = endMin(ev);
    const kind = kinds.find(k => k.id === ev.typeId);
    const typeLabel = kind?.label ?? String(ev.typeId);
    const canDelete =
      role !== 'VIEWER' &&
      ( (role === 'ADMIN' || (!!userId && ev.createdById === userId)) &&
        (role !== 'PLANNER' || canPlannerEdit) );
    return (
      <div
        key={ev.id}
        data-evt="1"
        className="group absolute flex select-none flex-col gap-0.5 rounded-xl border px-3 will-change-transform hover:shadow"
        style={{
          top: 2 + lane * (96 + 8),
          height: 96,
          left: `${(s / 1440) * 100}%`,
          width: widthForRange(s, e),
          touchAction: "none",
          backgroundColor: kind?.bg || "#e5e7eb",
          borderColor: kind?.border || "#9ca3af",
          zIndex: extra?.z ?? 1,
          opacity: extra?.ghost ? 0.6 : 1,
        }}
        title={`${typeLabel}\n${fmt(ev)}${ev.note ? `\n${ev.note}` : ""}`}
        onPointerDown={(pe) => startDragEvent(pe, ev)}
      >
        <div onPointerDown={(pe) => startResize(pe, ev, "left")} className="absolute left-0 top-0 h-full w-2 cursor-col-resize rounded-l-xl bg-black/0 hover:bg-black/10" style={{ touchAction: "none" }} data-ctrl="1" />
        <div onPointerDown={(pe) => startResize(pe, ev, "right")} className="absolute right-0 top-0 h-full w-2 cursor-col-resize rounded-r-xl bg-black/0 hover:bg-black/10" style={{ touchAction: "none" }} data-ctrl="1" />

        <div className="flex items-center gap-2">
          <div className="truncate text-sm font-medium">{typeLabel}</div>
          <div className="text-xs opacity-70">{fmt(ev)}</div>
          <div className="ml-auto flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100" data-ctrl="1">
            <button
              className="shrink-0 rounded border bg-white/70 px-1 py-0.5 hover:bg-white"
              aria-label="Upravit"
              title="Upravit"
              data-ctrl="1"
              onClick={(e2) => {
                e2.stopPropagation();
                if (!canEditNow) { denyMessage(); return; }
                onEdit(ev);
              }}
            >
              ✎
            </button>
            {canDelete && (
              <button
                className="shrink-0 rounded border bg-white/70 px-1 py-0.5 hover:bg-white"
                aria-label="Smazat"
                title="Smazat"
                data-ctrl="1"
                onClick={(e2) => {
                  e2.stopPropagation();
                  if (!canEditNow) { denyMessage(); return; }
                  if (window.confirm("Smazat blok?")) onDelete(ev.id);
                }}
              >
                🗑
              </button>
            )}
          </div>
        </div>

        {ev.note && (
          <div className="text-[11px] italic text-gray-700/80" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {ev.note}
          </div>
        )}
      </div>
    );
  }

  function fmtSelectionWidth(a: number, b: number) {
    if (a === b && a === 0) return "100%";
    return `${(Math.abs(b - a) / 1440) * 100}%`;
  }

  return (
    <div className="rounded-2xl border bg-white">
      <div className="flex items-stretch">
        <div className="flex w-40 shrink-0 items-start gap-2 border-r p-3">
          <div className="grow">
            <div className="font-medium">{motor.name}</div>
            <div className="text-xs text-gray-500">klik &amp; táhni • posun • resize</div>
          </div>
          {onDeleteMotor && (
            <button className="h-7 w-7 rounded-lg border text-gray-600 hover:bg-gray-50" title="Smazat motor" onClick={() => onDeleteMotor(motor.id)}>×</button>
          )}
        </div>

        <div
          className="relative grow select-none p-2"
          ref={containerRef}
          onPointerDown={handleBgPointerDown}
          style={{ height: rowInnerHeight + 16 }}
        >
          <div className="pointer-events-none grid" style={{ gridTemplateColumns: `repeat(${slotCount}, minmax(0, 1fr))` }}>
            {Array.from({ length: slotCount }, (_, i) => (
              <div key={i} className="relative" style={{ height: rowInnerHeight }}>
                <div className="absolute inset-y-0 right-0 w-px bg-gray-100" />
              </div>
            ))}
          </div>

          {select && (
            <div
              className="pointer-events-none absolute left-0 top-2 rounded-xl border-2 border-dashed border-emerald-500 bg-emerald-200/30"
              style={{
                height: rowInnerHeight - 4,
                left: `${(Math.min(select.startMin, select.endMin) / 1440) * 100}%`,
                width: fmtSelectionWidth(Math.min(select.startMin, select.endMin), Math.max(select.startMin, select.endMin)),
              }}
            />
          )}

          {lanesBase.map(({ ev, lane }) => renderBox(ev, lane))}

          {draggingId && (() => {
            const ev = events.find((x) => x.id === draggingId);
            if (!ev) return null;
            return renderBox(ev, dragLaneRef.current, { z: 10 });
          })()}
        </div>
      </div>
    </div>
  );
}
